import React from 'react';
import {Map, List} from 'immutable';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'sg/Button/Button.react';
import Checkbox from 'generic/Forms/Checkbox/Checkbox.react';
import SimpleModal from 'generic/SimpleModal/SimpleModal.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import './guide-selector.scss';

export default class GuideSelector extends React.Component {
  static propTypes = {
    label: PropTypes.string,
    guides: PropTypes.instanceOf(List),
    onSelect: PropTypes.func,
    defaultSubject: PropTypes.object,
    defaultGuideLevel: PropTypes.object
  };

  static defaultProps = {
    onSelect: () => {},
    label: 'Guide Level'
  };

  constructor(props) {
    super(props);

    const defaultPath = props.defaultGuideLevel
      ? List(props.defaultGuideLevel.getLevel().split('.')).unshift(
          props.defaultSubject.getId().replace(/-/g, '')
        )
      : List();

    this.state = {
      showModal: false,
      guideLevel: Map(),
      path: defaultPath
    };
  }

  showModal = () => {
    this.setState({
      showModal: true
    });
  };

  closeModal = () => {
    this.setState({
      showModal: false
    });
  };

  onSelect = (selectedGuideLevel) => {
    const subjectId = selectedGuideLevel.get('subjectId');
    const guideLevelId = selectedGuideLevel.get('guideLevelId');

    this.props.onSelect(
      Map({
        subjectId,
        guideLevelId: subjectId !== guideLevelId ? guideLevelId : ''
      })
    );

    const subject = this.props.guides.find(
      (currentSubject) => currentSubject.getId() === subjectId
    );
    const guideLevel = subject
      .getGuides()
      .first()
      .getGuideLevelById(guideLevelId);

    const path = guideLevel
      ? List(guideLevel.getLevel().split('.')).unshift(subject.getId().replace(/-/g, ''))
      : List();

    this.setState({
      guideLevel: Map({
        subject,
        guideLevel: guideLevel || null
      }),
      path
    });
  };

  render() {
    const subject = this.state.guideLevel.get('subject', this.props.defaultSubject);
    const guideLevel = this.state.guideLevel.get('guideLevel', this.props.defaultGuideLevel);

    const chooseClass = classnames('guide-selector__choose', {
      'guide-selector__choose--dark': subject && !guideLevel
    });

    const colon = guideLevel ? ':' : '';
    const chooseText = subject ? `${subject.getName()}${colon}` : 'Choose';

    return (
      <div className='guide-selector'>
        <div className='a-form-input__label'>{this.props.label}</div>
        <Button className='guide-selector__button unbutton' onClick={this.showModal}>
          <div className={chooseClass}>
            {chooseText} <span className='fa fa-arrow-circle-right' />
          </div>
          {guideLevel ? (
            <div className='guide-selector__selected-guide-level'>
              <MarkdownRendererV2 text={guideLevel.getName()} />
            </div>
          ) : null}
        </Button>
        {this.state.showModal ? (
          <GuideSelectorModal
            closeModal={this.closeModal}
            guides={this.props.guides}
            onSelect={this.onSelect}
            defaultSubjectId={subject ? subject.getId() : null}
            defaultGuideLevelId={guideLevel ? guideLevel.getId() : null}
            path={this.state.path}
          />
        ) : null}
      </div>
    );
  }
}

class GuideSelectorModal extends React.Component {
  static propTypes = {
    closeModal: PropTypes.func,
    guides: PropTypes.object,
    onSelect: PropTypes.func,
    defaultSubjectId: PropTypes.string,
    defaultGuideLevelId: PropTypes.string,
    path: PropTypes.instanceOf(List)
  };

  constructor() {
    super();

    this.state = {
      guideLevel: Map()
    };
  }

  selectGuideLevel = (guideLevelId, subjectId) => {
    this.setState({
      guideLevel: Map({
        subjectId,
        guideLevelId
      })
    });
  };

  chooseSelection = () => {
    if (!this.state.guideLevel.isEmpty()) {
      this.props.onSelect(this.state.guideLevel);
    }

    this.props.closeModal();
  };

  isSelectedRow(id) {
    const defaultCheckedId = this.props.defaultGuideLevelId || this.props.defaultSubjectId;
    const checkedId =
      this.state.guideLevel.get('guideLevelId') || this.state.guideLevel.get('subjectId');

    if (!checkedId) {
      return id === defaultCheckedId;
    }

    return id === checkedId;
  }

  makeGuideLevelRowProps({level, subjectId, canToggle = false, disabled = false}) {
    const headerLevel = level.get('nlevel', 0);
    const defaultOpen =
      this.props.path.size > headerLevel + 1 &&
      this.props.path.includes(level.getId().replace(/-/g, ''));

    return {
      subjectId,
      canToggle,
      defaultOpen,
      headerLevel,
      disabled,
      key: level.getId(),
      title: level.getName(),
      guideLevelId: level.getId(),
      selectGuideLevel: this.selectGuideLevel,
      isChecked: this.isSelectedRow(level.getId())
    };
  }

  generateSubtopicRows(subjectId, subtopics) {
    return subtopics.map((subtopic) => {
      return <GuideLevelRow {...this.makeGuideLevelRowProps({level: subtopic, subjectId})} />;
    });
  }

  generateTopicRows(guide, subjectId, themeId, topics) {
    return topics.map((topic) => {
      const subtopics = guide.getSortedSubtopics(themeId, topic.getId());
      const canToggle = !subtopics.isEmpty();

      return (
        <GuideLevelRow {...this.makeGuideLevelRowProps({level: topic, subjectId, canToggle})}>
          {this.generateSubtopicRows(subjectId, subtopics)}
        </GuideLevelRow>
      );
    });
  }

  generateThemeRows(guide, subjectId, themes) {
    return themes.map((theme) => {
      const topics = guide.getSortedTopics(theme.getId());
      const canToggle = !topics.isEmpty();

      return (
        <GuideLevelRow {...this.makeGuideLevelRowProps({level: theme, subjectId, canToggle})}>
          {this.generateTopicRows(guide, subjectId, theme.getId(), topics)}
        </GuideLevelRow>
      );
    });
  }

  generateSubjectRows() {
    return this.props.guides.map((subject) => {
      const subjectId = subject.getId();
      const guide = subject.getGuides().first();
      if (!guide) {
        return (
          <GuideLevelRow
            {...this.makeGuideLevelRowProps({level: subject, subjectId, disabled: true})}
          />
        );
      }

      const themes = guide.getSortedThemes();
      const canToggle = !themes.isEmpty();

      return (
        <GuideLevelRow {...this.makeGuideLevelRowProps({level: subject, subjectId, canToggle})}>
          {this.generateThemeRows(guide, subject.getId(), themes)}
        </GuideLevelRow>
      );
    });
  }

  render() {
    const showSelectedCount = this.props.defaultSubjectId || !this.state.guideLevel.isEmpty();

    return (
      <SimpleModal
        title='Subjects'
        className='guide-selector__modal'
        onModalClose={this.props.closeModal}
        footer={
          <div className='guide-selector__selection'>
            {showSelectedCount ? <span>1 Selected</span> : null}
            <Button text='Choose' color='green' onClick={this.chooseSelection} />
          </div>
        }
      >
        {this.generateSubjectRows()}
      </SimpleModal>
    );
  }
}

class GuideLevelRow extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    title: PropTypes.string,
    canToggle: PropTypes.bool,
    headerLevel: PropTypes.number,
    selectGuideLevel: PropTypes.func,
    guideLevelId: PropTypes.string,
    subjectId: PropTypes.string,
    isChecked: PropTypes.bool,
    defaultOpen: PropTypes.bool,
    disabled: PropTypes.bool
  };

  static defaultProps = {
    canToggle: false,
    disabled: false
  };

  constructor(props) {
    super(props);

    this.state = {
      contentVisible: props.defaultOpen
    };
  }

  toggleContent = () => {
    this.setState((state) => ({
      contentVisible: !state.contentVisible
    }));
  };

  render() {
    const classNames = classnames('guide-level-row__content', {
      'guide-level-row__content--hidden': !this.state.contentVisible
    });

    const buttonClasses = classnames('guide-level-row__button unbutton fa', {
      'fa-angle-right': !this.state.contentVisible,
      'fa-angle-down': this.state.contentVisible
    });

    const headerClasses = classnames('guide-level-row__header', {
      'guide-level-row__header--subject': this.props.headerLevel === 0,
      'guide-level-row__header--theme': this.props.headerLevel === 1,
      'guide-level-row__header--topic': this.props.headerLevel === 2,
      'guide-level-row__header--subtopic': this.props.headerLevel === 3,
      'guide-level-row__header--disabled': this.props.disabled
    });

    return (
      <div className='guide-selector-level-row'>
        <div className={headerClasses}>
          <Checkbox
            className='guide-level-row__checkbox'
            disabled={this.props.disabled}
            onChange={() => {
              this.props.selectGuideLevel(this.props.guideLevelId, this.props.subjectId);
            }}
            checked={this.props.isChecked}
          />
          {this.props.canToggle ? (
            <button type='button' className={buttonClasses} onClick={this.toggleContent} />
          ) : null}
          <div className='guide-level-row__title'>
            <MarkdownRendererV2 text={this.props.title} />
            {this.props.disabled && (
              <div className='guide-level-row__details'>
                This subject does not have a practice guide. To view assessment results, select
                Assessments as the report type.
              </div>
            )}
          </div>
        </div>
        <hr />
        <div className={classNames}>{this.props.children}</div>
      </div>
    );
  }
}
